import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Checkbox, Form, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useState } from "react";
import { useInRouterContext, useLocation, useNavigate } from "react-router";

import {
    loadEnterprisePage,
    deleteEnterprisePage,
    type EnterprisePageResult,
    type EnterpriseQuery,
    type EnterpriseRow
} from "./enterpriseManageAdapter";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import { SORT } from "@/meta/common";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import { loadGroupCompanies } from "@/views/pages/supplierManagement/SupplierPage";
import type { GroupCompanyOption } from "@/views/pages/supplierManagement/types";
import { ENTERPRISE_SHOW_LOCATION } from "@zhihaoscm/meta";
import { confirmDialog } from "@/utils/confirmDialog";

const defaultQuery: EnterpriseQuery = { page: 1, size: 10 };
const supplierTypeLabels: Record<number, string> = {
    1: "矿山",
    2: "砂场",
    3: "矿山加工厂",
    4: "贸易商",
    5: "其他"
};

/** 将旧 Vue 企业列表创建时间排序转换为分页接口参数。 */
export function buildEnterpriseSortQuery(
    query: EnterpriseQuery,
    sort: { prop?: string; order: "ascending" | "descending" | null }
): EnterpriseQuery {
    const nextQuery = { ...query, page: 1 };
    delete nextQuery.sortKey;
    delete nextQuery.sortOrder;
    if (sort.prop === "createdTime" && sort.order) {
        nextQuery.sortKey = "created_time";
        nextQuery.sortOrder = SORT.getIntroByCode(sort.order);
    }
    return nextQuery;
}

export interface EnterpriseManagePageProps {
    loadPage?: (query: EnterpriseQuery) => Promise<EnterprisePageResult>;
    deletePage?: (id: number | string) => Promise<unknown>;
    loadGroups?: (name: string) => Promise<GroupCompanyOption[]>;
    permissions?: string[];
}

/** 企业管理列表，迁移 Vue 的筛选、分页、空态、失败反馈和详情入口。 */
export function EnterpriseManagePage(props: EnterpriseManagePageProps) {
    return <EnterpriseManageRouterBoundary {...props} />;
}

/** 允许纯组件测试在无 Router 环境下验证列表空态和失败反馈。 */
function EnterpriseManageRouterBoundary(props: EnterpriseManagePageProps) {
    const inRouter = useInRouterContext();
    return inRouter ? <EnterpriseManageRoutedPage {...props} /> : <EnterpriseManageStandalonePage {...props} />;
}

function EnterpriseManageRoutedPage(props: EnterpriseManagePageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    return (
        <EnterpriseManageView
            {...props}
            isTradeEntry={location.pathname.startsWith("/tradeManage/")}
            navigate={navigate}
        />
    );
}

function EnterpriseManageStandalonePage(props: EnterpriseManagePageProps) {
    const navigate = (path: string) => window.history.pushState({}, "", path);
    return <EnterpriseManageView {...props} navigate={navigate} />;
}

function EnterpriseManageView({
    loadPage = loadEnterprisePage,
    deletePage = deleteEnterprisePage,
    loadGroups = loadGroupCompanies,
    permissions,
    isTradeEntry = false,
    navigate
}: EnterpriseManagePageProps & { isTradeEntry?: boolean; navigate: (path: string) => void }) {
    const [query, setQuery] = useState<EnterpriseQuery>(defaultQuery);
    const [rows, setRows] = useState<EnterpriseRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [groupOptions, setGroupOptions] = useState<GroupCompanyOption[]>([]);
    const [groupLoading, setGroupLoading] = useState(false);
    const [enterpriseSupplierOnly, setEnterpriseSupplierOnly] = useState(false);
    const [filterForm] = Form.useForm();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeButtonBanned = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    const buttonBanned = permissions ? permissions.includes("ROLE_BUTTON_BAN") : storeButtonBanned;

    useEffect(() => {
        let disposed = false;
        void loadPage(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                // 失败时保留当前页面和筛选参数，避免错误后无法恢复。
                setRows([]);
                setTotal(0);
                setErrorMessage("企业列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadPage, query]);

    /** 旧 Vue sort-change 触发后回到第一页并保留企业筛选条件。 */
    const handleSortChange = useCallback((sort: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => buildEnterpriseSortQuery(current, sort));
    }, []);

    /** 远程加载旧 Vue 企业筛选中的集团候选项。 */
    const searchGroups = async (value: string) => {
        if (!value.trim()) {
            setGroupOptions([]);
            return;
        }
        setGroupLoading(true);
        try {
            setGroupOptions((await loadGroups(value.trim())) || []);
        } catch {
            setGroupOptions([]);
        } finally {
            setGroupLoading(false);
        }
    };

    /** 删除企业并刷新当前查询，保持旧 Vue 操作列契约。 */
    const removeEnterprise = async (id?: number | string) => {
        if (id == null || !deletePage) return;
        try {
            await deletePage(id);
            setQuery(current => ({ ...current }));
            setErrorMessage(null);
        } catch {
            setErrorMessage("企业删除失败");
        }
    };

    /** 复用旧 Vue enterpriseManage 服务的统一删除确认文案。 */
    const confirmRemoveEnterprise = async (id?: number | string) => {
        if (id == null || !(await confirmDialog("是否删除"))) return;
        await removeEnterprise(id);
    };

    // 企业列表沿用 Vue 的供应商字段、固定列宽和操作插槽，并统一通过 zhihao-ui-react 列渲染。
    const columns: TableColumnProps<EnterpriseRow>[] = [
        { label: "序号", width: 78, prop: "code", render: ({ row }) => row.supplier?.code || "--" },
        {
            label: "供应商名称",
            minWidth: 120,
            prop: "supplierName",
            render: ({ row }) => row.supplier?.supplierName || "--"
        },
        {
            label: "供应商类型",
            width: 120,
            prop: "type",
            render: ({ row }) => supplierTypeLabels[Number(row.supplier?.type)] || "--"
        },
        {
            label: "所属集团",
            minWidth: 106,
            prop: "groupCompanyName",
            render: ({ row }) => row.supplier?.groupCompanyName || "--"
        },
        {
            label: "法人姓名",
            width: 106,
            prop: "legalRepresentative",
            render: ({ row }) => row.supplier?.legalRepresentative || "--"
        },
        {
            label: "统一社会信用代码",
            width: 204,
            prop: "socialCreditCode",
            render: ({ row }) => row.supplier?.socialCreditCode || "--"
        },
        {
            label: "所在地",
            minWidth: 92,
            prop: "systemName",
            render: ({ row }) => [row.supplier?.province, row.supplier?.city].filter(Boolean).join("") || "--"
        },
        {
            label: "展示位置",
            width: 148,
            prop: "showLocation",
            render: ({ row }) => ENTERPRISE_SHOW_LOCATION.getIntroByCode(Number(row.supplier?.showLocation)) || "--"
        },
        {
            label: "创建时间",
            width: 154,
            prop: "createdTime",
            sortable: "custom",
            render: ({ row }) => dateFormat(row.supplier?.createdTime, DATETIME_PICKER_FORMAT)
        },
        {
            label: "操作",
            fixed: "right",
            // Vue 固定操作列使用 240px，确保编辑/删除动作不被挤压。
            width: 240,
            prop: "operation",
            className: "admin-table-operation-column compact",
            render: ({ row }) =>
                row.supplier?.id != null ? (
                    <Space>
                        <Button
                            aria-label="查看企业详情"
                            type="link"
                            onClick={() =>
                                void navigate(`/tradeManage/enterpriseManage/detail?id=${row.supplier?.id ?? ""}`)
                            }
                        >
                            查看详情
                        </Button>
                        {canWrite ? (
                            <>
                                <Button
                                    aria-label="修改企业"
                                    disabled={buttonBanned}
                                    type="link"
                                    onClick={() =>
                                        void navigate(`/tradeManage/enterpriseManage/edit?id=${row.supplier?.id || ""}`)
                                    }
                                >
                                    修改
                                </Button>
                                {!row.productConsignment?.length ? (
                                    <Button
                                        aria-label="删除企业"
                                        disabled={buttonBanned}
                                        type="link"
                                        onClick={() => void confirmRemoveEnterprise(row.supplier?.id)}
                                    >
                                        删除
                                    </Button>
                                ) : null}
                            </>
                        ) : null}
                    </Space>
                ) : null
        }
    ];

    return (
        <section
            aria-label="enterprise-manage-page"
            className={`admin-trade-list-page enterprise-manage-page${isTradeEntry ? " is-trade-entry" : ""}`}
        >
            <Card>
                <ZhTable
                    border
                    columns={normalizeOperationColumns(columns, rows)}
                    data={rows}
                    onSortChange={handleSortChange}
                    pagination={false}
                    rowKey={row => String(row.supplier?.id || row.supplier?.code)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                {isTradeEntry ? "供应商管理" : "企业管理"}
                            </Typography.Title>

                            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}

                            <Form
                                className="admin-table-filter-form"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => {
                                    const nextQuery: EnterpriseQuery = {
                                        page: 1,
                                        size: query.size,
                                        ...(values.supplierName?.trim()
                                            ? { supplierName: values.supplierName.trim() }
                                            : {}),
                                        ...(enterpriseSupplierOnly
                                            ? { type: [1] }
                                            : values.type?.length
                                              ? { type: values.type }
                                              : {}),
                                        ...(values.groupCompanyId
                                            ? { groupCompanyId: Number(values.groupCompanyId) }
                                            : {})
                                    };
                                    setQuery(nextQuery);
                                }}
                            >
                                {canWrite ? (
                                    <Button
                                        aria-label="新增企业"
                                        className="admin-table-primary-action"
                                        disabled={buttonBanned}
                                        type="primary"
                                        onClick={() => void navigate("/tradeManage/enterpriseManage/add")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item label="供应商名称" name="supplierName">
                                    <Input placeholder="请输入供应商名称搜索" />
                                </Form.Item>
                                <Checkbox
                                    aria-label="企业供应商"
                                    checked={enterpriseSupplierOnly}
                                    onChange={event => setEnterpriseSupplierOnly(event.target.checked)}
                                >
                                    企业供应商
                                </Checkbox>
                                <Form.Item label="所属集团" name="groupCompanyId">
                                    <Select
                                        allowClear
                                        aria-label="所属集团"
                                        className="supplier-filter-group"
                                        filterOption={false}
                                        loading={groupLoading}
                                        options={groupOptions.map(option => ({
                                            label: option.name,
                                            value: option.id
                                        }))}
                                        placeholder="请输入关键字选择集团搜索"
                                        showSearch
                                        onSearch={value => void searchGroups(value)}
                                    />
                                </Form.Item>
                                <Form.Item label="供应商类型" name="type">
                                    <Select
                                        allowClear
                                        aria-label="供应商类型"
                                        className="supplier-filter-type"
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={Object.entries(supplierTypeLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择供应商类型"
                                    />
                                </Form.Item>
                                <Button
                                    aria-label="查询企业"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置企业筛选"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={() => {
                                        filterForm.resetFields();
                                        setGroupOptions([]);
                                        setQuery(defaultQuery);
                                    }}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4"
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 切换每页条数后回到第一页，普通翻页保留当前页。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default EnterpriseManagePage;
